import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Package, MapPin, Phone, User, Truck, Copy, ExternalLink,
  CheckCircle2, Clock, ShieldCheck, PackageCheck, Box, AlertCircle, Coins, CreditCard, Wallet,
  Upload, Image as ImageIcon, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';
import { useAuth } from '../contexts/AuthContext';

const playfairFont = "'Playfair Display', serif";

const STATUS_FLOW = [
  { key: 'awaiting_shipping_quote', label: 'Menunggu Quote Ongkir', icon: AlertCircle },
  { key: 'pending_payment',  label: 'Menunggu Pembayaran', icon: Clock },
  { key: 'payment_submitted', label: 'Bukti Terkirim — Menunggu Verifikasi', icon: ShieldCheck },
  { key: 'payment_verified', label: 'Pembayaran Diverifikasi', icon: ShieldCheck },
  { key: 'processing',       label: 'Diproses',            icon: Package },
  { key: 'packed',           label: 'Dikemas',             icon: Box },
  { key: 'shipped',          label: 'Dikirim',             icon: Truck },
  { key: 'delivered',        label: 'Diterima',            icon: PackageCheck },
  { key: 'completed',        label: 'Selesai',             icon: CheckCircle2 },
];

const STATUS_TONE = {
  awaiting_shipping_quote: { bg: 'rgba(245,158,11,0.18)', fg: '#F59E0B' },
  pending_payment: { bg: 'rgba(245,158,11,0.15)', fg: '#F59E0B' },
  payment_submitted:{ bg: 'rgba(168,85,247,0.18)', fg: '#A855F7' },
  payment_verified:{ bg: 'rgba(59,130,246,0.15)', fg: '#3B82F6' },
  processing:      { bg: 'rgba(59,130,246,0.15)', fg: '#3B82F6' },
  packed:          { bg: 'rgba(168,85,247,0.15)', fg: '#A855F7' },
  shipped:         { bg: 'rgba(212,175,55,0.18)', fg: '#D4AF37' },
  delivered:       { bg: 'rgba(34,197,94,0.15)',  fg: '#22C55E' },
  completed:       { bg: 'rgba(34,197,94,0.2)',   fg: '#22C55E' },
  cancelled:       { bg: 'rgba(239,68,68,0.15)',  fg: '#EF4444' },
};

const labelOf = (s) =>
  STATUS_FLOW.find((x) => x.key === s)?.label || (s === 'cancelled' ? 'Dibatalkan' : (s || ''));

const formatDateTime = (d) =>
  new Date(d).toLocaleString('id-ID', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const courierTrackUrl = (courier, tn) => {
  if (!tn) return null;
  const c = (courier || '').toLowerCase();
  if (c === 'jne') return `https://www.jne.co.id/en/tracking-result?awb=${encodeURIComponent(tn)}`;
  if (c.includes('jnt') || c.includes('j&t')) return `https://www.jet.co.id/track?awb=${encodeURIComponent(tn)}`;
  if (c.includes('sicepat')) return `https://www.sicepat.com/checkAwb?awb=${encodeURIComponent(tn)}`;
  if (c.includes('pos')) return `https://www.posindonesia.co.id/id/tracking/${encodeURIComponent(tn)}`;
  if (c.includes('anteraja')) return `https://anteraja.id/tracking/${encodeURIComponent(tn)}`;
  // Fallback: aggregator
  return `https://cekresi.com/?awb=${encodeURIComponent(tn)}&kurir=${encodeURIComponent(c || 'jne')}`;
};

const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };
const formatPrice = (price) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(Number(price) || 0);

export const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState('qris');
  const [paymentSettings, setPaymentSettings] = useState({ qris_image_url: '', bank_name: 'Mandiri', mandiri_account_number: '', mandiri_account_holder: '' });
  const [showInstructions, setShowInstructions] = useState(false); // true after customer clicks "Konfirmasi" for QRIS/Bank
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState('');
  const [uploadingProof, setUploadingProof] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const [orderRes, settingsRes] = await Promise.all([
          axios.get(`${API}/orders/${orderId}`, { withCredentials: true }),
          axios.get(`${API}/payment-methods`).catch(() => ({ data: {} })),
        ]);
        setOrder(orderRes.data);
        setPaymentSettings({
          qris_image_url: settingsRes.data?.qris_image_url || '',
          bank_name: settingsRes.data?.bank_name || 'Mandiri',
          mandiri_account_number: settingsRes.data?.mandiri_account_number || '',
          mandiri_account_holder: settingsRes.data?.mandiri_account_holder || '',
        });
        // If order is already in pending_payment with payment_method set, auto-show instructions
        if (orderRes.data?.status === 'pending_payment' && (orderRes.data?.payment_method === 'qris' || orderRes.data?.payment_method === 'bank_transfer')) {
          setPayMethod(orderRes.data.payment_method);
          setShowInstructions(true);
        }
      } catch (e) {
        console.error('Failed to fetch order:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  // Copy tracking number — passed in directly to avoid closing over state
  const copyTracking = useCallback(async (tn) => {
    if (!tn) return;
    try {
      await navigator.clipboard.writeText(tn);
      toast.success('Nomor resi disalin');
    } catch {
      toast.error('Gagal menyalin');
    }
  }, []);

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      toast.success(`${label || 'Disalin'}`);
    } catch {
      toast.error('Gagal menyalin');
    }
  };

  const handlePay = async () => {
    if (!payMethod) {
      toast.error('Pilih metode pembayaran');
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(
        `${API}/orders/${orderId}/pay`,
        { payment_method: payMethod },
        { withCredentials: true }
      );
      setOrder(data);
      if (payMethod === 'coin') {
        toast.success('Pembayaran coin berhasil! Pesanan diverifikasi.');
        checkAuth && checkAuth();
      } else {
        setShowInstructions(true);
        toast.success(`Metode ${payMethod === 'qris' ? 'QRIS' : 'Transfer Bank'} dipilih. Ikuti instruksi transfer di bawah.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pembayaran gagal');
    } finally {
      setPaying(false);
    }
  };

  const handleProofFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) {
      toast.error('Format harus JPG, JPEG, PNG atau WEBP');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 20MB');
      return;
    }
    setProofFile(f);
    setProofPreview(URL.createObjectURL(f));
  };

  const handleSubmitProof = async () => {
    if (!proofFile) {
      toast.error('Upload bukti transfer dulu');
      return;
    }
    setUploadingProof(true);
    try {
      // 1) Upload file → get URL
      const fd = new FormData();
      fd.append('file', proofFile);
      const upRes = await axios.post(`${API}/uploads/payment-proof`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const proof_url = upRes.data?.url;
      if (!proof_url) throw new Error('Upload gagal');
      // 2) Submit proof to order
      const { data } = await axios.post(
        `${API}/orders/${orderId}/submit-payment-proof`,
        { payment_method: payMethod, payment_proof_url: proof_url },
        { withCredentials: true }
      );
      setOrder(data);
      setProofFile(null);
      setProofPreview('');
      toast.success('Bukti transfer berhasil dikirim. Mohon tunggu verifikasi admin.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengirim bukti transfer');
    } finally {
      setUploadingProof(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A' }}>
        <div className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A', color: '#A3B8AC' }}>
        <div className="text-center">
          <p className="mb-4">Order not found</p>
          <button onClick={() => navigate('/profile')} className="px-6 py-2 rounded-full font-semibold" style={{ background: '#D4AF37', color: '#080C0A' }}>
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  const status = order.status || 'pending_payment';
  const tone = STATUS_TONE[status] || STATUS_TONE.pending_payment;
  const tn = order.tracking_number || '';
  const trackUrl = courierTrackUrl(order.tracking_courier || order.shipping_courier, tn);
  const history = Array.isArray(order.status_history) ? order.status_history : [];
  const sortedHistory = [...history].sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <div className="min-h-screen pb-12" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light flex items-center gap-3" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <Package className="w-6 h-6" style={{ color: '#D4AF37' }} /> Order Detail
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-4">
        {/* ID + Status pill */}
        <div className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle}>
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-sm" style={{ color: '#A3B8AC' }}>Order ID</p>
              <p className="font-mono font-semibold" style={{ color: '#F8FAF9' }} data-testid="order-id">#{order.id?.slice(0, 12)}</p>
            </div>
            <span
              className="px-3 py-1 rounded-full text-sm font-semibold"
              style={{ background: tone.bg, color: tone.fg }}
              data-testid="order-status"
            >
              {labelOf(status)}
            </span>
          </div>
          <p className="text-sm" style={{ color: '#A3B8AC' }} data-testid="order-date">{formatDateTime(order.created_at)}</p>
        </div>

        {/* ===== Awaiting Shipping Quote Notice ===== */}
        {status === 'awaiting_shipping_quote' && (
          <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)' }} data-testid="awaiting-quote-card">
            <div className="flex gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0" style={{ color: '#F59E0B' }} />
              <div className="space-y-1">
                <p className="font-semibold" style={{ color: '#F59E0B' }}>Menunggu Quote Ongkir dari Admin</p>
                <p className="text-sm" style={{ color: '#CFE0D5' }}>
                  Pesanan Anda sudah dibuat. Admin akan menghitung ongkos kirim manual berdasarkan alamat & berat. Anda akan diberi notifikasi total pembayaran agar bisa memilih metode (QRIS / Transfer Bank / Coin).
                </p>
                <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>
                  Subtotal pesanan: <span style={{ color: '#D4AF37', fontWeight: 600 }}>{formatPrice(order.subtotal || 0)}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ===== Pay Now (pending_payment) ===== */}
        {status === 'pending_payment' && (
          <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.3)' }} data-testid="pay-now-card">
            <h2 className="text-lg font-light mb-3 flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
              <CreditCard className="w-5 h-5" style={{ color: '#D4AF37' }} /> Pilih Metode Pembayaran
            </h2>
            <p className="text-xs mb-4" style={{ color: '#A3B8AC' }}>
              Total tagihan: <span style={{ color: '#D4AF37', fontWeight: 600, fontSize: '1rem' }}>{formatPrice(order.total_amount || 0)}</span>
              {order.shipping_cost > 0 && (
                <span className="ml-2">· Sudah termasuk ongkir {formatPrice(order.shipping_cost)} ({(order.shipping_courier || '').toUpperCase()} {order.shipping_service || ''})</span>
              )}
            </p>
            <div className="space-y-2">
              {(() => {
                const total = order.total_amount || 0;
                const coinNeeded = Math.max(1, Math.ceil(total / 1000));
                const userCoins = (user && user.coin_balance) || 0;
                const opts = [
                  { key: 'qris', label: 'QRIS', desc: `Transfer ${formatPrice(total)} via QRIS`, icon: CreditCard },
                  { key: 'bank_transfer', label: 'Transfer Bank', desc: `Transfer ${formatPrice(total)} ke rekening admin`, icon: Wallet },
                  { key: 'coin', label: 'Bayar dengan Coin', desc: `Pakai ${coinNeeded} Coin (saldo Anda: ${userCoins.toLocaleString('id-ID')})`, icon: Coins, insufficient: userCoins < coinNeeded || !user },
                ];
                return opts.map(({ key, label, desc, icon: Icon, insufficient }) => (
                  <button
                    key={key}
                    type="button"
                    disabled={insufficient}
                    onClick={() => { if (!insufficient) { setPayMethod(key); setShowInstructions(false); } }}
                    className="w-full rounded-xl p-3 text-left transition-all flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      background: payMethod === key ? 'rgba(212,175,55,0.18)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${payMethod === key ? '#D4AF37' : 'rgba(255,255,255,0.1)'}`,
                    }}
                    data-testid={`pay-method-${key}-btn`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color: payMethod === key ? '#D4AF37' : '#A3B8AC' }} />
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: '#F8FAF9' }}>{label}</p>
                      <p className="text-xs" style={{ color: '#A3B8AC' }}>{desc}</p>
                      {insufficient && key === 'coin' && (
                        <p className="text-[10px] mt-0.5" style={{ color: '#EF4444' }}>Saldo coin tidak mencukupi.</p>
                      )}
                    </div>
                  </button>
                ));
              })()}
            </div>
            {!showInstructions && (
              <button
                onClick={handlePay}
                disabled={paying}
                className="mt-4 w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#D4AF37', color: '#080C0A' }}
                data-testid="pay-order-btn"
              >
                {paying ? 'Memproses…' : (payMethod === 'coin'
                  ? `Bayar ${Math.max(1, Math.ceil((order.total_amount || 0) / 1000))} Coin`
                  : `Konfirmasi & Lihat Instruksi Transfer`)}
              </button>
            )}

            {/* ===== Bank Transfer / QRIS Instructions ===== */}
            {showInstructions && payMethod === 'bank_transfer' && (
              <div className="mt-5 rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="bank-instruction-card">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <h3 className="font-semibold" style={{ color: '#F8FAF9' }}>Instruksi Transfer Bank</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.25)' }}>
                    <div>
                      <p className="text-[11px]" style={{ color: '#A3B8AC' }}>Bank</p>
                      <p className="font-semibold" style={{ color: '#F8FAF9' }} data-testid="bank-name-display">{paymentSettings.bank_name || 'Mandiri'}</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.25)' }}>
                    <div className="flex-1">
                      <p className="text-[11px]" style={{ color: '#A3B8AC' }}>Nomor Rekening</p>
                      <p className="font-mono font-semibold text-lg" style={{ color: '#D4AF37' }} data-testid="bank-account-display">
                        {paymentSettings.mandiri_account_number || <span style={{ color: '#EF4444', fontSize: '0.85rem' }}>Belum diset admin</span>}
                      </p>
                    </div>
                    {paymentSettings.mandiri_account_number && (
                      <button onClick={() => copyText(paymentSettings.mandiri_account_number, 'Nomor rekening disalin')} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }} data-testid="copy-account-btn">
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.25)' }}>
                    <p className="text-[11px]" style={{ color: '#A3B8AC' }}>Atas Nama</p>
                    <p className="font-semibold" style={{ color: '#F8FAF9' }} data-testid="account-holder-display">{paymentSettings.mandiri_account_holder || '-'}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: 'rgba(212,175,55,0.12)', border: '1px solid rgba(212,175,55,0.3)' }}>
                    <p className="text-[11px]" style={{ color: '#A3B8AC' }}>Total Transfer</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="font-bold text-xl" style={{ color: '#D4AF37' }} data-testid="bank-amount-display">{formatPrice(order.total_amount || 0)}</p>
                      <button onClick={() => copyText(String(order.total_amount || 0), 'Nominal disalin')} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }} data-testid="copy-amount-btn">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
                    <p className="text-xs" style={{ color: '#FECACA' }}>Transfer sesuai nominal agar mudah diverifikasi admin.</p>
                  </div>
                </div>
              </div>
            )}

            {showInstructions && payMethod === 'qris' && (
              <div className="mt-5 rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="qris-instruction-card">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <h3 className="font-semibold" style={{ color: '#F8FAF9' }}>Scan QRIS untuk Bayar</h3>
                </div>
                {paymentSettings.qris_image_url ? (
                  <div className="flex flex-col items-center gap-3">
                    <img src={paymentSettings.qris_image_url} alt="QRIS" className="w-64 h-64 object-contain rounded-lg" style={{ background: '#FFF', padding: '8px' }} data-testid="qris-image" />
                    <p className="text-sm" style={{ color: '#A3B8AC' }}>Total Transfer:</p>
                    <p className="font-bold text-xl" style={{ color: '#D4AF37' }} data-testid="qris-amount-display">{formatPrice(order.total_amount || 0)}</p>
                  </div>
                ) : (
                  <p className="text-sm text-center py-4" style={{ color: '#EF4444' }}>QRIS belum diset admin. Mohon hubungi admin atau pilih metode lain.</p>
                )}
                <div className="mt-3 flex gap-2 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
                  <p className="text-xs" style={{ color: '#FECACA' }}>Transfer sesuai nominal agar mudah diverifikasi admin.</p>
                </div>
              </div>
            )}

            {/* ===== Upload Proof ===== */}
            {showInstructions && (payMethod === 'qris' || payMethod === 'bank_transfer') && (
              <div className="mt-4 rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="upload-proof-card">
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="w-5 h-5" style={{ color: '#D4AF37' }} />
                  <h3 className="font-semibold" style={{ color: '#F8FAF9' }}>Upload Bukti Transfer</h3>
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}>Wajib</span>
                </div>
                <p className="text-xs mb-3" style={{ color: '#A3B8AC' }}>Format: JPG, JPEG, PNG, atau WEBP (maks 20MB).</p>
                <label className="block cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleProofFileChange}
                    className="hidden"
                    data-testid="proof-file-input"
                  />
                  <div className="rounded-lg border-2 border-dashed p-6 text-center transition-all hover:border-[#D4AF37]" style={{ borderColor: proofPreview ? '#D4AF37' : 'rgba(255,255,255,0.2)' }}>
                    {proofPreview ? (
                      <div className="space-y-2">
                        <img src={proofPreview} alt="preview" className="max-h-48 mx-auto rounded-lg object-contain" data-testid="proof-preview-img" />
                        <p className="text-xs" style={{ color: '#A3B8AC' }}>{proofFile?.name}</p>
                        <p className="text-[11px]" style={{ color: '#D4AF37' }}>Klik untuk ganti gambar</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <ImageIcon className="w-10 h-10 mx-auto" style={{ color: '#A3B8AC' }} />
                        <p className="text-sm" style={{ color: '#F8FAF9' }}>Klik untuk pilih gambar bukti transfer</p>
                        <p className="text-[11px]" style={{ color: '#A3B8AC' }}>JPG, JPEG, PNG, WEBP</p>
                      </div>
                    )}
                  </div>
                </label>
                <button
                  onClick={handleSubmitProof}
                  disabled={!proofFile || uploadingProof}
                  className="mt-4 w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: '#D4AF37', color: '#080C0A' }}
                  data-testid="submit-proof-btn"
                >
                  <Upload className="w-4 h-4" />
                  {uploadingProof ? 'Mengirim…' : 'Kirim Bukti Transfer'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ===== Payment Submitted (waiting for admin verification) ===== */}
        {status === 'payment_submitted' && (
          <div className="rounded-2xl p-6 backdrop-blur-xl" style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.35)' }} data-testid="payment-submitted-card">
            <div className="flex gap-3">
              <ShieldCheck className="w-6 h-6 flex-shrink-0" style={{ color: '#A855F7' }} />
              <div className="space-y-2 flex-1">
                <p className="font-semibold" style={{ color: '#A855F7' }}>Bukti Transfer Berhasil Dikirim</p>
                <p className="text-sm" style={{ color: '#CFE0D5' }}>
                  Mohon tunggu verifikasi admin. Anda akan diberi notifikasi setelah admin memvalidasi pembayaran.
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <div><span style={{ color: '#A3B8AC' }}>Metode: </span><span style={{ color: '#F8FAF9' }}>{(order.payment_method || '-').toUpperCase().replace('_', ' ')}</span></div>
                  <div><span style={{ color: '#A3B8AC' }}>Total: </span><span style={{ color: '#D4AF37', fontWeight: 600 }}>{formatPrice(order.total_amount || 0)}</span></div>
                </div>
                {order.payment_proof_url && (
                  <div className="mt-2">
                    <p className="text-xs mb-2" style={{ color: '#A3B8AC' }}>Bukti yang dikirim:</p>
                    <img src={order.payment_proof_url} alt="proof" className="max-h-48 rounded-lg object-contain" style={{ background: 'rgba(255,255,255,0.05)' }} data-testid="submitted-proof-img" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Shipment Information */}
        <div className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle} data-testid="shipment-info-card">
          <h2 className="text-lg font-light mb-4 flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <Truck className="w-5 h-5" style={{ color: '#D4AF37' }} /> Shipment Information
          </h2>
          {tn ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs" style={{ color: '#A3B8AC' }}>Courier</p>
                  <p className="font-medium" style={{ color: '#F8FAF9' }} data-testid="ship-courier">
                    {(order.tracking_courier || order.shipping_courier || '-').toUpperCase()}
                  </p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: '#A3B8AC' }}>Service</p>
                  <p className="font-medium" style={{ color: '#F8FAF9' }} data-testid="ship-service">
                    {(order.tracking_service || order.shipping_service || '-').toUpperCase()}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs" style={{ color: '#A3B8AC' }}>Nomor Resi</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="font-mono font-semibold" style={{ color: '#D4AF37' }} data-testid="ship-tracking">{tn}</p>
                    <button onClick={() => copyTracking(tn)} className="p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="copy-tracking-btn" title="Salin nomor resi">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-xs" style={{ color: '#A3B8AC' }}>Shipping Status</p>
                  <p className="font-medium" style={{ color: tone.fg }}>{labelOf(status)}</p>
                </div>
                {order.tracking_updated_at && (
                  <div>
                    <p className="text-xs" style={{ color: '#A3B8AC' }}>Last Update</p>
                    <p className="font-medium text-sm" style={{ color: '#F8FAF9' }} data-testid="ship-last-update">{formatDateTime(order.tracking_updated_at)}</p>
                  </div>
                )}
                {order.tracking_notes && (
                  <div className="col-span-2">
                    <p className="text-xs" style={{ color: '#A3B8AC' }}>Catatan Pengiriman</p>
                    <p className="text-sm" style={{ color: '#CFE0D5' }}>{order.tracking_notes}</p>
                  </div>
                )}
              </div>
              {trackUrl && (
                <a
                  href={trackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold mt-2"
                  style={{ background: '#D4AF37', color: '#080C0A' }}
                  data-testid="track-package-btn"
                >
                  <Truck className="w-4 h-4" /> Lacak Paket
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          ) : (
            <p className="text-sm" style={{ color: '#A3B8AC' }}>
              Belum ada informasi pengiriman. Nomor resi akan ditampilkan setelah paket dikirim.
            </p>
          )}
        </div>

        {/* Status timeline */}
        <div className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle} data-testid="status-history-card">
          <h2 className="text-lg font-light mb-4" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Timeline Status</h2>
          <ol className="relative border-l ml-2" style={{ borderColor: 'rgba(212,175,55,0.25)' }}>
            {sortedHistory.length === 0 ? (
              <p className="text-sm" style={{ color: '#A3B8AC' }}>Belum ada riwayat status.</p>
            ) : sortedHistory.map((h, i) => {
              const T = STATUS_FLOW.find((x) => x.key === h.status)?.icon || Clock;
              const isLast = i === sortedHistory.length - 1;
              const c = STATUS_TONE[h.status] || STATUS_TONE.pending_payment;
              return (
                <li key={i} className="ml-6 pb-5" data-testid={`timeline-${h.status}`}>
                  <span
                    className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full"
                    style={{ background: c.bg, border: `1px solid ${c.fg}` }}
                  >
                    <T className="w-3 h-3" style={{ color: c.fg }} />
                  </span>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-medium" style={{ color: isLast ? c.fg : '#F8FAF9' }}>{labelOf(h.status)}</span>
                    <span className="text-xs" style={{ color: '#A3B8AC' }}>{formatDateTime(h.at)}</span>
                  </div>
                  {h.note && <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>{h.note}</p>}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Shipping Information (recipient) */}
        <div className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle}>
          <h2 className="text-lg font-light mb-4" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Recipient</h2>
          <div className="space-y-3">
            <div className="flex gap-3">
              <User className="w-5 h-5 mt-0.5" style={{ color: '#D4AF37' }} />
              <div>
                <p className="text-sm" style={{ color: '#A3B8AC' }}>Recipient</p>
                <p className="font-medium" style={{ color: '#F8FAF9' }} data-testid="shipping-name">{order.shipping_name}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <MapPin className="w-5 h-5 mt-0.5" style={{ color: '#D4AF37' }} />
              <div>
                <p className="text-sm" style={{ color: '#A3B8AC' }}>Address</p>
                <p className="font-medium" style={{ color: '#F8FAF9' }} data-testid="shipping-address">{order.shipping_address}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Phone className="w-5 h-5 mt-0.5" style={{ color: '#D4AF37' }} />
              <div>
                <p className="text-sm" style={{ color: '#A3B8AC' }}>Phone</p>
                <p className="font-medium" style={{ color: '#F8FAF9' }} data-testid="shipping-phone">{order.shipping_phone}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="rounded-2xl p-6 backdrop-blur-xl" style={cardStyle}>
          <h2 className="text-lg font-light mb-4" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Products</h2>
          <div className="space-y-4">
            {order.items.map((item, idx) => (
              <div key={`${item.product_id}|${item.variant_key || ''}|${idx}`} className="flex gap-4" data-testid={`order-item-${item.product_id}`}>
                <img src={item.image_url} alt={item.name} className="w-20 h-20 object-cover rounded-xl" />
                <div className="flex-1">
                  <p className="font-medium" style={{ color: '#F8FAF9' }} data-testid="item-name">{item.name}</p>
                  {item.variant_label && (
                    <p className="text-xs" style={{ color: '#D4AF37' }}>{item.variant_label}</p>
                  )}
                  <p className="text-sm" style={{ color: '#A3B8AC' }}>Qty: {item.quantity}</p>
                  <p className="font-bold mt-1" style={{ color: '#D4AF37' }} data-testid="item-price">{formatPrice(item.price)}</p>
                </div>
                <p className="font-bold" style={{ color: '#D4AF37', fontFamily: playfairFont }} data-testid="item-total">{formatPrice(item.price * item.quantity)}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex justify-between" style={{ color: '#A3B8AC' }}>
              <span>Subtotal</span>
              <span>{formatPrice(order.subtotal ?? order.total_amount)}</span>
            </div>
            <div className="flex justify-between" style={{ color: '#A3B8AC' }}>
              <span>Shipping{order.shipping_courier ? ` (${order.shipping_courier.toUpperCase()} ${order.shipping_service || ''})` : ''}</span>
              <span style={{ color: (order.shipping_cost || 0) === 0 ? '#F59E0B' : '#F8FAF9' }}>
                {(order.shipping_cost || 0) === 0
                  ? (status === 'awaiting_shipping_quote' ? 'Menunggu admin' : 'FREE')
                  : formatPrice(order.shipping_cost)}
              </span>
            </div>
            {order.tickets_used > 0 && (
              <div className="flex justify-between" style={{ color: '#D4AF37' }}>
                <span>Tickets ({order.tickets_used})</span>
                <span>-{formatPrice(order.discount || order.tickets_used * 100)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <span style={{ color: '#F8FAF9' }}>Total</span>
              <span style={{ color: '#D4AF37', fontFamily: playfairFont }} data-testid="order-grand-total">{formatPrice(order.total_amount)}</span>
            </div>
            {order.cashback_tickets > 0 && (
              <div className="flex justify-between text-xs mt-1" style={{ color: '#22C55E' }}>
                <span>Cashback earned</span>
                <span>+{order.cashback_tickets} tickets</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
